import React, { useCallback, useMemo, useState } from "react";
import { BookmarkPlus, Inbox, X } from "lucide-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
    captureSavedView,
    loadSavedViews,
    persistSavedViews,
    snapshotFilters,
    viewsMatchSnapshot,
} from "../utils/taskSavedViews";
import { PINNED_TRIAGE_VIEWS, triageCounts } from "../utils/taskTriage";

const TaskSavedViewsBar = ({
    role = "admin",
    snapshot,
    onApply,
    tasks = [],
    inboxPin = "unassigned",
    onOpenInbox,
}) => {
    const isAdmin = role !== "staff";
    const [views, setViews] = useState(() => loadSavedViews(role));

    const current = useMemo(() => snapshotFilters(snapshot), [snapshot]);
    const counts = useMemo(() => (isAdmin ? triageCounts(tasks) : null), [isAdmin, tasks]);
    const activeId = views.find((view) => viewsMatchSnapshot(view, current))?.id;

    const persist = useCallback(
        (next) => {
            setViews(next);
            persistSavedViews(role, next);
        },
        [role]
    );

    const handleSave = async () => {
        const result = await Swal.fire({
            title: "Save this view",
            input: "text",
            inputPlaceholder: isAdmin ? "Unassigned high priority" : "Due today board",
            showCancelButton: true,
            confirmButtonText: "Save",
        });
        if (!result.isConfirmed) return;
        const name = String(result.value || "").trim();
        if (!name) {
            toast.error("View name is required");
            return;
        }
        persist([...views, captureSavedView(name, current)]);
        toast.success("View saved");
    };

    const handleRemove = (id, event) => {
        event?.stopPropagation();
        persist(views.filter((view) => view.id !== id));
    };

    return (
        <div className="task-saved-views" aria-label="Saved views">
            {isAdmin && (
                <div className="task-saved-views__pins" role="group" aria-label="Triage shortcuts">
                    {PINNED_TRIAGE_VIEWS.map((pin) => (
                        <button
                            key={pin.id}
                            type="button"
                            className={`task-saved-views__chip ${
                                snapshot?.viewMode === "inbox" && inboxPin === pin.id
                                    ? "is-active"
                                    : ""
                            }`}
                            onClick={() => onOpenInbox?.(pin.id)}
                        >
                            {pin.id === "unassigned" && <Inbox size={12} />}
                            {pin.label}
                            <span>{counts?.[pin.id] || 0}</span>
                        </button>
                    ))}
                </div>
            )}

            <div className="task-saved-views__row">
                {views.map((view) => (
                    <div
                        key={view.id}
                        className={`task-saved-views__chip ${activeId === view.id ? "is-active" : ""}`}
                    >
                        <button
                            type="button"
                            className="task-saved-views__chip-label"
                            onClick={() => onApply?.(view)}
                            title={view.name}
                        >
                            {view.name}
                        </button>
                        <button
                            type="button"
                            className="task-saved-views__remove"
                            aria-label={`Delete ${view.name}`}
                            onClick={(event) => handleRemove(view.id, event)}
                        >
                            <X size={12} />
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    className="task-saved-views__save"
                    onClick={handleSave}
                >
                    <BookmarkPlus size={14} />
                    Save view
                </button>
            </div>
        </div>
    );
};

export default TaskSavedViewsBar;
